// File: frontend/src/components/ai/AgentDetailsModal.js

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Bot, MessageSquare } from 'lucide-react';
import LoadingSpinner from '../utility/LoadingSpinner';
import styles from './AgentDetailsModal.module.css';

const AgentDetailsModal = ({ 
  isOpen, 
  onClose, 
  agentDetails, 
  isLoading,
  onStartChat
}) => {
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getBadgeClass = (value, type) => {
    if (type === 'status') {
      switch (value) {
        case 'active': return styles.badgeSuccess;
        case 'inactive': return styles.badgeWarning;
        case 'deprecated': return styles.badgeError;
        default: return styles.badgeDefault;
      }
    }
    if (type === 'deployment') {
      switch (value) {
        case 'deployed': return styles.badgeSuccess;
        case 'pending': return styles.badgeWarning;
        case 'failed': return styles.badgeError;
        default: return styles.badgeDefault;
      }
    }
    return styles.badgeDefault;
  };

  const modalContent = (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <Bot size={24} />
            <h2 className={styles.modalTitle}>Agent Details</h2>
          </div>
          <button
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className={styles.modalBody} data-lenis-prevent>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <LoadingSpinner size="large" text="Loading agent details..." />
            </div>
          ) : agentDetails ? (
            <>
              {/* Basic Information */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <Bot size={18} />
                  Basic Information
                </h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Agent Name</span>
                    <span className={styles.detailValue}>{agentDetails.agent_name}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Version</span>
                    <span className={styles.detailValue}>{agentDetails.version}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Status</span>
                    <span className={`${styles.badge} ${getBadgeClass(agentDetails.agent_status, 'status')}`}>
                      {agentDetails.agent_status}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Deployment</span>
                    <span className={`${styles.badge} ${getBadgeClass(agentDetails.deployment_status, 'deployment')}`}>
                      {agentDetails.deployment_status}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Marketing Tagline</span>
                    <span className={styles.detailValue}>{agentDetails.marketing_tagline}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Product Stage</span>
                    <span className={styles.detailValue}>{agentDetails.product_stage}</span>
                  </div>
                </div>
              </section>

              {/* Description */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Description</h3>
                <p className={styles.description}>{agentDetails.description}</p>
              </section>

              {/* Model Configuration */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Model Configuration</h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Primary Model</span>
                    <span className={styles.detailValue}>{agentDetails.primary_model}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Provider</span>
                    <span className={styles.detailValue}>{agentDetails.primary_model_provider}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Temperature</span>
                    <span className={styles.detailValue}>{agentDetails.temperature}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Max Tokens</span>
                    <span className={styles.detailValue}>{agentDetails.max_tokens}</span>
                  </div>
                </div>
              </section>

              {/* Capabilities */}
              {agentDetails.capabilities && agentDetails.capabilities.length > 0 && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>Capabilities</h3>
                  <div className={styles.tagsList}>
                    {agentDetails.capabilities.map((cap, index) => (
                      <span key={index} className={styles.tag}>
                        {cap.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Supported Languages */}
              {agentDetails.supported_languages && agentDetails.supported_languages.length > 0 && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>Supported Languages</h3>
                  <div className={styles.tagsList}>
                    {agentDetails.supported_languages.map((lang, index) => (
                      <span key={index} className={styles.tag}>
                        {lang.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Timestamps */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Timestamps</h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Created</span>
                    <span className={styles.detailValue}>{formatDate(agentDetails.created_at)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Updated</span>
                    <span className={styles.detailValue}>{formatDate(agentDetails.updated_at)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Published</span>
                    <span className={styles.detailValue}>{formatDate(agentDetails.published_at)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Approved</span>
                    <span className={styles.detailValue}>{formatDate(agentDetails.approved_at)}</span>
                  </div>
                </div>
              </section>

              {/* Start Chat Button */}
              {agentDetails.agent_status === 'active' && (
                <div className={styles.chatButtonContainer}>
                  <button
                    onClick={() => onStartChat(agentDetails)}
                    className={styles.startChatButton}
                  >
                    <MessageSquare size={20} />
                    Start Chat with {agentDetails.agent_name}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className={styles.errorContainer}>
              <p>Failed to load agent details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AgentDetailsModal;